import { registerEventListeners } from '@shared/api/registerEventListeners';
import { Environments } from '@shared/environment/environments';
import {
  ConfigType,
  FilteredClientConfigType,
  InnerConfigObjectType,
  ListenerCallback,
} from '@shared/config/types';
import { filterConfig } from '@shared/config/helpers/filterConfig';
import { deepMerge } from '@shared/config/helpers/deepMerge';

/**
 * Initializes the setter and getter functions for Utiq configuration
 */
export const initializeConfigAPIFunctions = () => {
  /**
   * API method to get config entries
   * @returns An object with the config entries and the listeners set in the Utiq namespace
   */
  const getConfig = () => {
    return {
      listeners: window.Utiq!.listeners,
      ...window.Utiq!.config,
    };
  };

  /**
   * Utiq method to set config entries
   * @param configObject - user custom configuration object
   */
  const setConfig = (configObject: ConfigType) => {
    const { listeners, ...clientConfig } = configObject;

    /* Filtering to only allowed config entries to stop injecting custom configuration, e.g. utiqHost */
    const filteredClientConfig: FilteredClientConfigType =
      filterConfig(clientConfig);

    /* Setting of the final config object */
    window.Utiq ||= {};
    window.Utiq.config = deepMerge(
      window.Utiq.config as InnerConfigObjectType,
      filteredClientConfig as InnerConfigObjectType,
    );

    if (window.Utiq?.API?.addEventListener) {
      registerEventListeners(Environments.Utiq, listeners);
    } else if (listeners) {
      const mergedListeners: {
        [listener: string]: ListenerCallback[];
      } = {};

      const mergeListeners = ([listenerName, listener]: [
        string,
        ListenerCallback | ListenerCallback[],
      ]) => {
        if (typeof listener === 'function') {
          mergedListeners[listenerName] ||= [];
          mergedListeners[listenerName].push(listener);
        } else if (Array.isArray(listener)) {
          mergedListeners[listenerName] ||= [];
          mergedListeners[listenerName] =
            mergedListeners[listenerName].concat(listener);
        }
      };

      if (window.Utiq.config?.listeners) {
        Object.entries(window.Utiq.config.listeners).forEach(mergeListeners);
      }
      Object.entries(listeners).forEach(mergeListeners);
      window.Utiq.config!.listeners = mergedListeners;
    }
  };

  window.Utiq ||= {};
  window.Utiq.API ||= {};
  window.Utiq.API.getConfig = getConfig;
  window.Utiq.API.setConfig = setConfig;

  /**
   * Needed to execute the set config function before utiqLoader
   * to ensure all configurations are correctly set
   */
  if (window.Utiq?.queue) {
    window.Utiq.queue = window.Utiq.queue.filter((callback) => {
      if (callback.toString().includes('Utiq.API.setConfig')) {
        callback();
        return false;
      }
      return true;
    });
  }
};
